import { Box, IconButton, styled } from "@mui/material";
import React, {
  useEffect,
  useRef,
  useState,
} from "react";
import {
  borderRadius,
  borderSize,
  colors,
  fontSize,
  fontWeight,
  height,
  // iconSize,
  margin,
  width,
  // padding,
} from "theme/defaultStyle";
import CloseIcon from "theme/icons/CloseIcon";
import CustomTooltip from "../custom-tooltip";

interface Props {
  width: number;
  height: number;
  getImage: (image: string) => void;
  defaultImage: string | null;
  disabled?: boolean;
}

const CanvasWrapper = styled("div")<{ disabled?: boolean }>(({ disabled }) => ({
  position: "relative",
  width: "100%",
  // maxWidth: "740px",
  pointerEvents: disabled ? "none" : "auto",
}));

const ThumbnailText = styled("div")({
  alignItems: "center",
  color: colors.grey11,
  display: "flex",
  fontFamily: "Alex Brush",
  fontSize: fontSize.xlarge7,
  fontWeight: fontWeight.normal1,
  fontStyle: "italic",
  height: "100%",
  justifyContent: "center",
  position: "absolute",
  width: "100%",
});

const StyledCanvas = styled("canvas")({
  backgroundColor: "transparent",
  border: `${borderSize.xsmall} dashed ${colors.blue19}`,
  borderRadius: borderRadius.small,
  boxSizing: "border-box",
  position: "relative",
  width: "100%",
  height: "auto",
  // maxWidth: "740px",
  aspectRatio: "740 / 202",
  display: "block",
});

const Message = styled("div")({
  fontSize: fontSize.large,
  fontWeight: fontWeight.normal1,
  marginBottom: margin.small2,
});

export const ClearButtonContainer = styled(Box)({
  position: "absolute",
  top: "7px",
  right: "3px",
  "& .MuiIconButton-root": {
    padding: "2px",
    "& .MuiSvgIcon-root": {
      color: `${colors.blue01} !important`,
      fill: `${colors.blue01} !important`,
      width: width.xxsmall0,
      height: height.xxsmall0,
    },
  },
});



function StewartDrawZone({ height, width, getImage, defaultImage, disabled = false }: Props) {
  const [isDrawing, setIsDrawing] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const isInitializingRef = useRef(true);
  const hasDrawnRef = useRef(false);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Get scaled coordinates based on actual canvas size vs display size
  const getScaledCoordinates = (offsetX: number, offsetY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: offsetX, y: offsetY };

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: offsetX * scaleX,
      y: offsetY * scaleY
    };
  };

  const startDrawing = ({ nativeEvent }: any) => {
    const context = canvasRef.current?.getContext("2d");
    const { offsetX, offsetY } = nativeEvent;
    const scaled = getScaledCoordinates(offsetX, offsetY);
    context?.beginPath();
    context?.moveTo(scaled.x, scaled.y);
    setIsDrawing(true);
    hasDrawnRef.current = false;
  };

  const finishDrawing = () => {
    const context = canvasRef.current?.getContext("2d");
    context?.closePath();
    setIsDrawing(false);
    // Only generate image if actual drawing occurred
    if (hasDrawnRef.current) {
      setIsEmpty(false);
      generateSignatureImage();
    }
    hasDrawnRef.current = false;
  };

  const draw = ({ nativeEvent }: any) => {
    if (!isDrawing) return;
    const context = canvasRef.current?.getContext("2d");
    const { offsetX, offsetY } = nativeEvent;
    const scaled = getScaledCoordinates(offsetX, offsetY);
    context?.lineTo(scaled.x, scaled.y);
    context?.stroke();
    // Mark that actual drawing has occurred
    hasDrawnRef.current = true;
  };

  const generateSignatureImage = async () => {
    const imageSrc = canvasRef.current?.toDataURL("image/png");
    getImage(imageSrc as string);
  };

  const clear = () => {
    const context = canvasRef.current?.getContext("2d");
    context?.clearRect(0, 0, width, height);

    setIsEmpty(true);
    isInitializingRef.current = true;

    getImage("");
  };



  // const scaleCanvasContext = (context: CanvasRenderingContext2D) => {
  //   // Remove transformation - we'll handle scaling through getScaledCoordinates
  //   context?.setTransform(1, 0, 0, 1, 0, 0);
  // };

  const cloneImage = (imageSrc: string) => {
    const canvas = canvasRef.current;
    const context = canvasRef.current?.getContext("2d");
    if (!context || !canvas) return;

    let img = new Image();
    img.src = imageSrc;
    img.onload = function () {
      context.clearRect(0, 0, canvas.width, canvas.height);

      // Calculate scale to fit image maintaining aspect ratio
      var scale = Math.min(
        canvas.width / img.width,
        canvas.height / img.height
      );

      // Center the image
      var x = (canvas.width - img.width * scale) / 2;
      var y = (canvas.height - img.height * scale) / 2;

      context.drawImage(img, x, y, img.width * scale, img.height * scale);
    };
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!context || !canvas) return;
    context.lineCap = "round";
    context.strokeStyle = "black";
    context.lineWidth = 2;
  }, []);

  useEffect(() => {
    if (!defaultImage) return;

    if (isInitializingRef.current) {
      cloneImage(defaultImage);
      setIsEmpty(false);
      isInitializingRef.current = false;
    }
  }, [defaultImage]);

  return (
    <CanvasWrapper disabled={disabled} style={{ position: "relative" }}>
      <Message></Message>
      {isEmpty && <ThumbnailText>Draw Signature here</ThumbnailText>}

      <StyledCanvas
        ref={canvasRef}
        width={width}
        height={height}
        onMouseDown={disabled ? undefined : startDrawing}
        onMouseUp={disabled ? undefined : finishDrawing}
        onMouseMove={disabled ? undefined : draw}
        onMouseLeave={disabled ? undefined : finishDrawing}
      />

      {!isEmpty && !disabled && (
        <ClearButtonContainer onClick={clear}>
          <CustomTooltip
            title="Clear Signature"
            placement="bottom"
            arrow
          >
            <IconButton
              onClick={clear}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </CustomTooltip>
        </ClearButtonContainer>
      )}
    </CanvasWrapper>
  );
}

export default StewartDrawZone;
